import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import { useField } from 'formik';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';
import PersonAvatar, {
	PersonAvatarSize,
} from 'packages/kakapo-ui-kit/components/users/PersonAvatar';
import { WhiteButton } from 'packages/kakapo-ui-kit/components/buttons/WhiteButton';
import { ButtonSize } from 'packages/kakapo-ui-kit/types/Button';
import PrimaryButton from 'packages/kakapo-ui-kit/components/buttons/PrimaryButton';

import EvaluationSubmissionItemInput from './input/EvaluationSubmissionItemInput';

const Card = styled(Box)`
	border-radius: 4px;
	padding: 10px;
	background: white;
	box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.1), 0px 8px 16px rgba(0, 0, 0, 0.1);
`;

export default function EvaluationAnswerComposer(props: {
	optimistic?: boolean;
	section: number;
	item: number;
	metadata?: any;
	submission: graphql.EvaluationSubmission;
	result: graphql.EvaluationSubmissionResult;
	onSave: () => void;
	onDismiss: () => void;
}) {
	const { t } = useTranslation();

	const item = props.submission.evaluationForm.sections[props.section].items[props.item];
	const name = `sections[${props.section}].items[${props.item}]`;

	const [, { value }, { setValue }] = useField(props.optimistic ? name : 'pessimist.' + name);
	const [, , { setValue: setActualValue }] = useField(name);
	const [, { value: initialValue }] = useField(name);

	const discard = () => {
		setValue(undefined);
		props.onDismiss();
	};

	const done = () => {
		if (value === undefined) {
			discard();
			return;
		}

		setActualValue({ ...initialValue, ...value });
		setValue(undefined);
		props.onSave();
		props.onDismiss();
	};

	const Container = props.optimistic ? Box : Card;

	return (
		<Container>
			<Flex
				alignItems={
					item.type === graphql.EvaluationSectionItemType.Competency ? 'center' : 'flex-start'
				}
			>
				{props.submission.viewer.hasShared && (
					<Box mr={2} flexShrink={0} flexGrow={0}>
						<PersonAvatar size={PersonAvatarSize.Medium} person={props.result.user} />
					</Box>
				)}
				<Box flexShrink={1} flexGrow={1}>
					<EvaluationSubmissionItemInput
						submission={props.submission}
						item={item}
						metadata={props.metadata}
						name={props.optimistic ? name : 'pessimist.' + name}
						initialValue={initialValue}
						autoFocus={!props.optimistic}
						party={props.result.party}
					/>
				</Box>
			</Flex>
			{props.optimistic ? null : (
				<Flex justifyContent="flex-end" mt={2}>
					<Box mr={2}>
						<WhiteButton size={ButtonSize.Small} onClick={() => discard()}>
							{t('common.cancel')}
						</WhiteButton>
					</Box>
					<PrimaryButton size={ButtonSize.Small} onClick={() => done()}>
						{t('common.done')}
					</PrimaryButton>
				</Flex>
			)}
		</Container>
	);
}
